import React, { useState } from "react";
import { Disclosure } from "@headlessui/react";
import { FiFilter } from "react-icons/fi";

const AuditTemplates = () => {
  // === 1) Internal Audit Templates Data ===
  const templates = [
    {
      title: "Internal Audit Engagement Work Program",
      description: "Template for planning internal audits, including objectives, scope, resources, etc.",
      phase: "Planning",
      category: "Internal Audit Plan",
      subphase: "Initial Planning",
      documentUrl: "/templates/audit/internal_audit_workprogram.pdf"
    },

    {
      title: "Fieldwork Checklist",
      description: "A checklist for conducting fieldwork during internal audits.",
      phase: "Fieldwork",
      category: "Checklist",
      subphase: "On-Site Evaluation",
      documentUrl: "/templates/fieldwork-checklist.pdf"
    },
    {
      title: "Audit Report Template",
      description: "A structured template for reporting audit findings.",
      phase: "Reporting",
      category: "Report",
      subphase: "Draft Reporting",
      documentUrl: "/templates/audit-report-template.pdf"
    },
    {
      title: "Follow-Up Action Plan",
      description: "Template for follow-up actions after an audit.",
      phase: "Follow-up",
      category: "Action Plan",
      subphase: "Post-Audit Follow-Up",
      documentUrl: "/templates/followup-action-plan.pdf"
    }
    // ... add additional templates as needed
  ];

  // === 2) Filter States ===
  const [selectedPhase, setSelectedPhase] = useState("");
  const [selectedSubphase, setSelectedSubphase] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // === 3) Unique Filter Values ===
  const uniquePhases = [...new Set(templates.map((t) => t.phase))];
  const uniqueSubphases = selectedPhase
    ? [...new Set(templates.filter((t) => t.phase === selectedPhase).map((t) => t.subphase))]
    : [];

  // === 4) Filter the Templates ===
  const filteredTemplates = templates.filter((t) => {
    const matchesPhase = !selectedPhase || t.phase === selectedPhase;
    const matchesSubphase = !selectedSubphase || t.subphase === selectedSubphase;
    const matchesSearch =
      !searchQuery || t.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPhase && matchesSubphase && matchesSearch;
  });

  return (
    <div className="flex flex-col lg:flex-row">
      {/* Filter Panel for Desktop */}
      <aside className="lg:w-64 bg-gray-100 p-4 rounded-lg shadow-md hidden lg:block">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Filters</h3>
        {/* Phase Filter */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-600">Phase</h4>
          <select
            className="w-full mt-2 p-2 border rounded-md"
            value={selectedPhase}
            onChange={(e) => {
              setSelectedPhase(e.target.value);
              setSelectedSubphase("");
            }}
          >
            <option value="">All Phases</option>
            {uniquePhases.map((phase) => (
              <option key={phase} value={phase}>
                {phase}
              </option>
            ))}
          </select>
        </div>
        {/* Subphase Filter */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-600">Subphase</h4>
          <select
            className="w-full mt-2 p-2 border rounded-md"
            value={selectedSubphase}
            onChange={(e) => setSelectedSubphase(e.target.value)}
            disabled={!selectedPhase}
          >
            <option value="">All Subphases</option>
            {uniqueSubphases.map((subphase) => (
              <option key={subphase} value={subphase}>
                {subphase}
              </option>
            ))}
          </select>
        </div>
        {/* Search Filter */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-600">Search Title</h4>
          <input
            type="text"
            className="w-full mt-2 p-2 border rounded-md"
            placeholder="Search by title"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </aside>

      {/* Mobile Filter Toggle */}
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="lg:hidden flex items-center bg-blue-500 text-white px-4 py-2 rounded-md shadow-md w-full mb-4">
              <FiFilter className="mr-2" />
              {open ? "Hide Filters" : "Show Filters"}
            </Disclosure.Button>
            <Disclosure.Panel className="p-4 bg-gray-100 rounded-lg shadow-md lg:hidden">
              {/* Phase Filter */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-600">Phase</h4>
                <select
                  className="w-full mt-2 p-2 border rounded-md"
                  value={selectedPhase}
                  onChange={(e) => {
                    setSelectedPhase(e.target.value);
                    setSelectedSubphase("");
                  }}
                >
                  <option value="">All Phases</option>
                  {uniquePhases.map((phase) => (
                    <option key={phase} value={phase}>
                      {phase}
                    </option>
                  ))}
                </select>
              </div>
              {/* Subphase Filter */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-600">Subphase</h4>
                <select
                  className="w-full mt-2 p-2 border rounded-md"
                  value={selectedSubphase}
                  onChange={(e) => setSelectedSubphase(e.target.value)}
                  disabled={!selectedPhase}
                >
                  <option value="">All Subphases</option>
                  {uniqueSubphases.map((subphase) => (
                    <option key={subphase} value={subphase}>
                      {subphase}
                    </option>
                  ))}
                </select>
              </div>
              {/* Search Filter */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-600">Search Title</h4>
                <input
                  type="text"
                  className="w-full mt-2 p-2 border rounded-md"
                  placeholder="Search by title"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Templates Grid */}
      <div className="flex-1 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Internal Audit Templates</h2>
        <p className="text-gray-600">
          Explore templates categorized by audit phases and subphases.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {filteredTemplates.map((template, index) => (
            <div
              key={index}
              className="bg-gray-200 rounded-lg p-4 flex flex-col items-center justify-center shadow-md"
            >
              <h3 className="text-lg font-semibold text-gray-700 text-center">
                {template.title}
              </h3>
              <p className="text-gray-600 text-sm text-center mt-2">
                {template.description}
              </p>
              <div className="text-gray-600 text-sm mt-2">
                <strong>Phase:</strong> {template.phase}
              </div>
              <div className="text-gray-600 text-sm">
                <strong>Subphase:</strong> {template.subphase}
              </div>
              <div className="text-gray-600 text-sm mt-2">
                <strong>Category:</strong> {template.category}
              </div>
              <a
                href={template.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
              >
                View Document
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuditTemplates;
